import type { PartialNullable } from "../../Extensions";
import { $$, type IJNativeEvent } from "../../JNative";
import type { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { SvgHelper } from "../SvgHelper";
import { Link, type ILink, type LinkPropertyValues } from "./Link";
import { NAMESPACE } from "./_Namespace";

import "./Button.scss";

export enum ButtonModeEnum
{
    normal = "normal",
    toggle = "toggle",
}
export enum ButtonAlignmentEnum
{
    left = "left",
    right = "right"
}

export type ButtonPropertyValues =
    LinkPropertyValues
    & {
        icon: string;
        iconToggle: string;
        iconAlignment: string;
        mode: ButtonModeEnum;
        showLabel: boolean;
        state: boolean;
        svgKey: string;
        svgKeyToggle: string;
    };

export type ButtonProperties = {
    [name in keyof ButtonPropertyValues]: Property<ButtonPropertyValues[name]>
};

export interface IButton extends ILink, ButtonProperties
{
}

export class Button extends Link implements IButton
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Button";
    public static override className = "Button";

    // #region Constructor

    static {
        $$(document).on("keydown", ".rad-control-button", (evt: IJNativeEvent<KeyboardEvent>) =>
        {
            switch (evt.originalEvent.key)
            {
                case "Enter":
                    {
                        $$(evt.target)
                            .radControl()
                            .click();

                        break;
                    }
            }
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ButtonPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                mode: ButtonModeEnum.normal,
                showLabel: true,
                state: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _iconElementRef!: ElementRef;
    protected _svgWrapperElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public icon!: PropertyString;
    public iconToggle!: PropertyString;
    public iconAlignment!: PropertyString;
    public mode!: Property<ButtonModeEnum>;
    public showLabel!: PropertyBoolean;
    public state!: PropertyBoolean;
    public svgKey!: PropertyString;
    public svgKeyToggle!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ButtonPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-button ui button icon");

        // Trigger Property Changes
        this.icon.triggerChange();
        this.iconAlignment.triggerChange();
        this.showLabel.triggerChange();
        this.svgKey.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _iconAlignmentChanged(change: PropertyChange<string>)
    {
        const
            $$element = this.$$element.get();

        if (change.currentValue === "right")
        {
            $$element?.append(this._iconElementRef.current);
            $$element?.append(this._svgWrapperElementRef.current);
        }
        else
        {
            $$element?.prepend(this._iconElementRef.current);
            $$element?.prepend(this._svgWrapperElementRef.current);
        }
    }
    protected _iconChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _iconToggleChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _modeChanged(change: PropertyChange<ButtonModeEnum>)
    {
        /* Intentionally empty */
    }
    protected _showLabelChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("labeled");
        else
            this.removeCssClass("labeled");
    }
    protected _stateChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-on");
        else
            this.removeCssClass("rad-state-on");

        this._updateLayout();

        this.triggerHandler("stateChanged", change);
    }
    protected _svgKeyChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _svgKeyToggleChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _updateLayout()
    {
        const
            state = this.state.get(),
            icon = state ? this.iconToggle.get() : this.icon.get(),
            svgKey = state ? this.svgKeyToggle.get() : this.svgKey.get();

        this.removeCssClass("rad-icon");
        this.removeCssClass("rad-linear-icon");
        this.removeCssClass("rad-svg-icon");
        this._iconElementRef.current?.removeClass();

        if (icon)
        {
            if (icon.startsWith("linear-icon-"))
            {
                this.addCssClass("rad-linear-icon");
                this._iconElementRef.current?.addClass(icon);
            }
            else
            {
                this.addCssClass("rad-icon");
                this._iconElementRef.current?.addClass(`icon ${icon}`);
            }
        }
        else
        {
            if (svgKey)
            {
                this.addCssClass("rad-svg-icon");
                this._svgWrapperElementRef.current?.empty();
                this._svgWrapperElementRef.current?.append(SvgHelper.createSvg(svgKey));
                this._svgWrapperElementRef.current?.show();
            }
            else
                this._svgWrapperElementRef.current?.hide();
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ButtonPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._iconElementRef = new ElementRef();
        this._svgWrapperElementRef = new ElementRef();

        this.icon = new PropertyString({
            context: this,
            initialValue: propertyValues.icon,
            changeHandler: this._iconChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Icon" }),
            ],
        });
        this.iconToggle = new PropertyString(this, propertyValues.iconToggle, this._iconToggleChanged);
        this.iconAlignment = new PropertyString(this, propertyValues.iconAlignment, this._iconAlignmentChanged);
        this.mode = new Property<ButtonModeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.mode, this._modeChanged);
        this.showLabel = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.showLabel,
            changeHandler: this._showLabelChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Show Label?" }),
            ],
        });
        this.state = new PropertyBoolean(this, propertyValues.state, this._stateChanged);
        this.svgKey = new PropertyString(this, propertyValues.svgKey, this._svgKeyChanged);
        this.svgKeyToggle = new PropertyString(this, propertyValues.svgKeyToggle, this._svgKeyToggleChanged);
    }

    protected override _createElementComplete()
    {
        super._createElementComplete();

        this._svgWrapperElementRef.current.hide();
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <a>
                <span ref={this._svgWrapperElementRef} class="rad-svg-wrapper">
                </span>
                <i ref={this._iconElementRef} class="icon">
                    <span></span>
                </i>
                <span ref={this._textElementRef} class="rad-text"></span>
                {children ?? this._createChildrenHtml()}
            </a>
        );
    }

    protected override onClick(evt: IJNativeEvent)
    {
        super.onClick(evt);

        const
            self = evt.data.self as this;

        if (self.mode.get() === "toggle")
            self.state.set(!self.state.get());
    }

    // #endregion

    // #endregion
}